import gql from 'graphql-tag';
import { useMemo, useRef } from "react";
import { useInView, motion } from "motion/react";
import { Splide, SplideTrack } from 'react-splide-ts';
import { RecentPostsQuery, CarouselStoriesFragment } from '@graphqlTypes';
import { CarouselPagination, Container, RevealWrapper, SectionHeader } from '@components';
import CarouselStoriesItem from './CarouselStoriesItem';
import { ACFMediaFragment } from '@/fragments';
import { useViewSize } from '@/providers/ViewSizeContext';

import { AnimVals } from '@/utils/animUtils';
import { useDebug } from '@/utils/useDebugLog';

import styles from './CarouselStories.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

const getPosts = (data: RecentPostsQuery['posts'] | CarouselStoriesFragment['posts']) => {
  return data.nodes.filter((node) => node.__typename === 'Post') as RecentPostsQuery['posts']['nodes'];
};

type CarouselStoriesProps = CarouselStoriesFragment & {
  recentStories: PageWrapperData['recentStories'];
};

export default function CarouselStories(props: CarouselStoriesProps) {
  const { log } = useDebug({ name: 'CarouselStories', bgColor: 'crimson', textColor: 'white', enabled: false });

  const { recentStories, headline, subhead, type, posts, cta } = props;
  const [carouselType] = type;
  const mostRecent = carouselType === 'most-recent';
  const showHeader = Boolean(headline || subhead);
  const stories = useMemo(() => {
    if (mostRecent) return recentStories?.nodes || [];
    return getPosts(posts);
  }, [mostRecent, posts, recentStories]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    speed: 500,
    flickMaxPages: 1,
    flickPower: 250,
    updateOnMove: true,

    // Desktop first (Splide default)
    perPage: 3,
    perMove: 3,
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1025px
      1025: {
        perPage: 2,
        perMove: 2,
        gap: '3rem',
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        perPage: 1,
        perMove: 1,
        fixedWidth: '76%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  };

  // Override accessibility attributes
  const onSplideMount = () => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  };

  // -------------------- Animation

  const rootEl = useRef<HTMLElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <section className={cx(['carousel-stories', `type-${carouselType}`])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>

        { showHeader && (
          <Container>
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isRevealEnabled
              isInView={isInView}
            />
          </Container>
        )}
        <motion.div className={cx('splide-container')}>
          <Splide className={cx('splide')}
            hasTrack={false}
            options={splideOptions}
            onMounted={onSplideMount}
          >
            <SplideTrack>
              {stories.map((story, index) => (
                <CarouselStoriesItem
                  story={story}
                  key={index}
                  index={index}
                />
              ))}
            </SplideTrack>
            <Container>
              <CarouselPagination cta={cta} className={cx('story-carousel-bottom')} />
            </Container>
          </Splide>
        </motion.div>

      </RevealWrapper>
    </section>
  );

}

CarouselStories.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment CarouselStories on FlexiblePageContentContentCarouselStoriesLayout {
      headline
      subhead
      type
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
      posts {
        nodes {
          ... on Post {
            id
            uri
            title
            pageData {
              featuredImage {
                ...ACFMedia
              }
            }
            featureData {
              excerpt
            }
          }
        }
      }
    }
  `,
};
